import React, { createContext, useContext, useState } from "react";
import { toast } from "react-toastify";

import { appSettings } from "../config/appConfig";
import { useUserContext } from "./UserContextProvider";
import { refreshMonitorData } from "../data/messageNotifierServices";
import { logOutPage } from "../components/utils/logOut.js";

const ConversationContext = createContext();

export const ConversationContextProvider = ({ children }) => {
  const { setIsLoading } = useUserContext();
  const baseUrl = appSettings.serverBaseUrl;
  const [isRecentChatLoaded, setRecentChatLoaded] = useState(false);

  const [recentContactsList, setRecentContactsList] = useState([]);
  const [chatContact, setChatContact] = useState("");
  const [selectedChatContactToShow, setSelectedChatContactToShow] =
    useState("");
  const [isShowChatHistory, setChatHistory] = useState(false);
  const [messageNotificationCount, setMessageNotificationCount] = useState(0);
  const [isNewNotification, setIsNewNotification] = useState(false);
  const [campaignRepliedStatuses, setCampaignRepliedStatuses] = useState([]);

  const [chatData, setChatData] = useState();
  const [chatImage, setChatImage] = useState();
  const [quickReplyList, setQuickReplyList] = useState();
  const [approvedQuickReplyList, setApprovedQuickReplyList] = useState();
  const [pendingApprovalQuickReply, setPendingApprovalQuickReply] = useState();
  const [notificationList, setNotificationList] = useState();
  const [currContact, setCurrContact] = useState("");

  const convGetChatContacts = async (
    searchText,
    pageNumber,
    removeFromContact
  ) => {
    setRecentChatLoaded(true);
    try {
      const response = await fetch(`${baseUrl}/conv/get-chat-contacts`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ searchText, pageNumber }),
      });

      const data = await response.json();

      if ("isValidToken" in data && !data?.isValidToken) {
        logOutPage();
        setRecentChatLoaded(false);
      } else {
        if (data?.Error) {
          toast.error(data.Error);
        } else {
          if (
            data?.recentContacts.length > 0 &&
            !("isEmpty" in (data?.recentContacts?.[0] || {}))
          ) {
            setRecentContactsList((prevMessages) => {
              const messagesMap = new Map(
                prevMessages?.map((message) => [message._id, message])
              );
              data.recentContacts.forEach((contact) => {
                messagesMap.set(contact._id, contact); // Updates existing or adds new
              });
              // Remove the specific ID
              if (removeFromContact && removeFromContact !== "") {
                messagesMap.delete(removeFromContact);
              }
              return Array.from(messagesMap.values());
            });

            setRecentChatLoaded(false);
          } else {
            if (
              "isEmpty" in (data?.recentContacts?.[0] || {}) &&
              pageNumber === 1
            ) {
              setRecentContactsList(data?.recentContacts);
              setRecentChatLoaded(false);
            } else {
              setRecentChatLoaded(false);
            }
          }
        }
      }
    } catch (err) {
      console.log("convGetChatContacts catch err", err);
      setRecentChatLoaded(false);
    }
  };

  const convGetAssignedChats = async (searchText) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${baseUrl}/conv/get-assigned-chats`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ searchText }),
      });
      const assignChatData = await response.json();
      if ("isValidToken" in assignChatData && !assignChatData?.isValidToken) {
        logOutPage();
      }
      setIsLoading(false);
      return assignChatData;
    } catch (err) {
      console.log("Error in convGetChatAssigns ", err);
      return [];
    }
  };

  const convGetChatHistory = (phoneNumber) => {
    setIsLoading(true);
    fetch(`${baseUrl}/conv/get-chat-history`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ phoneNumber }),
    })
      .then((res) => res.json())
      .then((response) => {
        if ("isValidToken" in response && !response?.isValidToken) {
          logOutPage();
        } else {
          if (response?.Error) {
            toast.error(response.Error);
          } else if (response?.data) {
            const responseData = JSON.parse(response?.data);
            let chatsData = [...responseData, {}];
            setChatData(chatsData);
            refreshMonitorData?.next("refreshRecentChat");
          }
        }
      })
      .catch((err) => {
        toast.error("Failed to retrieve chat history");
        console.log("convGetChatHistory err", err);
      });
    setIsLoading(false);
  };

  const updateMessageReadStatus = (phoneNumber) => {
    const readStatus = false;
    setIsLoading(true);
    fetch(`${baseUrl}/conv/update-message-read-status`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ phoneNumber, readStatus }),
    })
      .then((res) => res.json())
      .then((response) => {
        if ("isValidToken" in response && !response?.isValidToken) {
          logOutPage();
        } else {
          if (response?.Error) {
            toast.error(response.Error);
          } else {
            refreshMonitorData?.next("refreshRecentChat");
          }
        }
      })
      .catch((err) => {
        toast.error("Failed to update read status");
      });
    setIsLoading(false);
  };

  const convGetChatMedia = (mediaId) => {
    setIsLoading(true);
    fetch(`${baseUrl}/conv/get-chat-media`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ mediaId }),
    })
      .then((res) => res.json())
      .then((response) => {
        if ("isValidToken" in response && !response?.isValidToken) {
          logOutPage();
        } else {
          if (response?.Error) {
            toast.error(response.Error);
          } else if (response?.extractedMedia) {
            if (response?.mediaContentType?.includes("image")) {
              setChatImage(
                `data:${response.mediaContentType};base64,${response.extractedMedia}`
              );
            } else {
              const date = new Date();
              const currentTime = `${date.getDate()}${date.getHours()}${date.getMinutes()}`;

              const downloadLink = document.createElement("a");
              downloadLink.href = `data:${response.mediaContentType};base64,${response.extractedMedia}`;
              downloadLink.download = `wabm-download-${currentTime}`;
              downloadLink.click();
            }
          } else {
            toast.warn("Media unavailable");
            console.log("chat media response", response);
          }
        }
      })
      .catch((err) => {
        toast.error("Failed to fetch media");
      });
  };

  const getCampaignRepliedStatuses = () => {
    fetch(`${baseUrl}/conv/get-campaign-replied-statuses`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response) => {
        if ("isValidToken" in response && !response?.isValidToken) {
          logOutPage();
        } else {
          if (response?.Error) {
            toast.error(response.Error);
          } else {
            setCampaignRepliedStatuses(response?.data);
          }
        }
      })
      .catch((err) => {
        // toast.error("Couldn't fetch campaign statuses");
        console.log("Couldn't fetch campaign statuses");
      });
  };

  const saveQuickReply = (quickReplyData) => {
    setIsLoading(true);
    fetch(`${baseUrl}/conv/save-quick-reply`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(quickReplyData),
    })
      .then((res) => res.json())
      .then((response) => {
        if ("isValidToken" in response && !response?.isValidToken) {
          logOutPage();
        } else {
          if (response?.Error) {
            toast.error(response.Error);
          } else if (response?.message) {
            toast.success(response.message);
            getQuickRepliesList("all");
          }
        }
        setIsLoading(false);
      })
      .catch((err) => {
        toast.error("Failed to save quick reply");
        setIsLoading(false);
      });
  };

  const getQuickRepliesList = (filter) => {
    const filterParam = filter && filter !== "" ? `?filter=${filter}` : "";
    setIsLoading(true);
    fetch(`${baseUrl}/conv/get-quick-replies` + filterParam, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response) => {
        if ("isValidToken" in response && !response?.isValidToken) {
          logOutPage();
        } else {
          if (response?.Error) {
            toast.error(response.Error);
            setIsLoading(false);
          }

          if (filter === "all") {
            setQuickReplyList(response?.data);
          } else if (filter === "approved") {
            setApprovedQuickReplyList(response?.data);
          } else if (filter === "pending") {
            setPendingApprovalQuickReply(response?.data);
          }
        }
      })
      .catch((err) => {
        toast.error("Unable to get quick replies");
        setIsLoading(false);
      });
  };

  const approveQuickReply = (approveQuickReplyData) => {
    setIsLoading(true);
    fetch(`${baseUrl}/conv/approve-quick-reply`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(approveQuickReplyData),
    })
      .then((res) => res.json())
      .then((response) => {
        if ("isValidToken" in response && !response?.isValidToken) {
          logOutPage();
        } else {
          if (response?.Error) {
            toast.error(response.Error);
          } else if (response?.message) {
            toast.success(response.message);
          }
        }
        setIsLoading(false);
      })
      .catch((err) => {
        toast.error("Failed to approve quick reply");
        setIsLoading(false);
      });
  };

  const deleteQuickReply = (quickReplyId) => {
    setIsLoading(true);
    fetch(`${baseUrl}/conv/delete-quick-reply`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ id: quickReplyId }),
    })
      .then((res) => res.json())
      .then((response) => {
        if ("isValidToken" in response && !response?.isValidToken) {
          logOutPage();
        } else {
          if (response?.Error) {
            toast.error(response.Error);
          } else if (response?.message) {
            toast.success(response.message);
            getQuickRepliesList("all");
          }
        }
        setIsLoading(false);
      })
      .catch((err) => {
        toast.error("Failed to delete quick reply");
        setIsLoading(false);
      });
  };

  const assignChat = (phoneNumber, assignedUsersArray) => {
    setIsLoading(true);
    fetch(`${baseUrl}/conv/assign-chats`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        phone_number: phoneNumber,
        assignedUser: assignedUsersArray,
      }),
    })
      .then((res) => res.json())
      .then((response) => {
        if ("isValidToken" in response && !response?.isValidToken) {
          logOutPage();
        } else {
          if (response?.Error) {
            toast.error(response.Error);
          } else {
            convGetChatContacts({
              searchContact: "",
              searchName: "",
              selectedAgent: 0,
              selectedFromDate: "",
              selectedToDate: "",
              filterCriteria: "", // e.g. "unsaved", "not_replied", "interested"
            });
            toast.success(response?.message);
          }
        }
      })
      .catch((err) => {
        console.log("assignChat err", err);
        toast.error("Failed to Assign chat");
      });
    setIsLoading(false);
  };

  const getNotifications = () => {
    setIsLoading(true);
    fetch(`${baseUrl}/conv/get-notifications`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response) => {
        if ("isValidToken" in response && !response?.isValidToken) {
          logOutPage();
        } else {
          if (response?.Error) {
            toast.error(response.Error);
            setIsLoading(false);
          }
          setNotificationList(response?.data);
        }
      })
      .catch((err) => {
        toast.error("Unable to get notifications");
        setIsLoading(false);
      });
  };

  const saveWelcomeMessage = (welcomeMessages) => {
    setIsLoading(true);
    fetch(`${baseUrl}/conv/create-welcome-message`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(welcomeMessages),
    })
      .then((res) => res.json())
      .then((response) => {
        if ("isValidToken" in response && !response?.isValidToken) {
          logOutPage();
        } else {
          if (response?.Error) {
            toast.error(response.Error);
          } else if (response?.message) {
            toast.success(response.message);
            getWelcomeMessages();
          }
        }
        setIsLoading(false);
      })
      .catch((err) => {
        toast.error("Failed to save welcome message");
        setIsLoading(false);
      });
  };

  const deleteWelcomeMessage = (welcomeMessageId) => {
    setIsLoading(true);
    fetch(`${baseUrl}/conv/delete-welcome-message`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        messageId: welcomeMessageId,
      }),
    })
      .then((res) => res.json())
      .then((response) => {
        if ("isValidToken" in response && !response?.isValidToken) {
          logOutPage();
        } else {
          if (response?.Error) {
            toast.error(response.Error);
          } else if (response?.message) {
            toast.success(response.message);
            getWelcomeMessages();
          }
        }
        setIsLoading(false);
      })
      .catch((err) => {
        console.log("deleteWelcomeMessage err", err);
        toast.error("Failed to delete welcome message");
        setIsLoading(false);
      });
  };

  const changeWelcomeMessagesOrder = async (welcomeMessage) => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${baseUrl}/conv/change-welcome-messages-order`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ welcomeMessage }),
        }
      );
      const result = await response.json();
      if ("isValidToken" in result && !result?.isValidToken) {
        logOutPage();
      } else {
        if (result?.Error) {
          toast.error(result.Error);
        } else if (result?.message) {
          getWelcomeMessages();
          toast.success(result.message);
        }
      }
    } catch (err) {
      console.log("changeWelcomeMessagesOrder err", err);
      toast.error("Failed to change welcome message order");
    } finally {
      setIsLoading(false);
    }
  };

  const getWelcomeMessages = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${baseUrl}/conv/fetch-welcome-messages`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });
      const welcomeMessages = await response.json();
      if ("isValidToken" in welcomeMessages && !welcomeMessages?.isValidToken) {
        logOutPage();
      }
      setIsLoading(false);
      return welcomeMessages;
    } catch (err) {
      setIsLoading(false);
      console.log("getWelcomeMessages err ", err);
      return [];
    }
  };

  const createAutoReplyMessage = async (autoRepliesMessage) => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${baseUrl}/conv/create-auto-reply-message`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(autoRepliesMessage),
        }
      );

      const result = await response.json();
      if ("isValidToken" in result && !result?.isValidToken) {
        logOutPage();
      } else {
        if (result?.Error) {
          toast.error(result.Error);
        } else if (result?.message) {
          toast.success(result.message);
        }
        return result;
      }
    } catch (err) {
      toast.error("Failed to create auto reply message");
    } finally {
      setIsLoading(false);
    }
  };

  const toggleAutoReplyActive = async (messageId, isActive) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${baseUrl}/conv/toggle-auto-reply-active`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ messageId: messageId, isActive: isActive }),
      });
      const result = await response.json();
      if ("isValidToken" in result && !result?.isValidToken) {
        logOutPage();
      } else {
        if (result?.Error) {
          toast.error(result.Error);
        } else if (result?.message) {
          toast.success(result.message);
        }
      }
    } catch (err) {
      console.log("toggleAutoReplyActive err", err);
      toast.error("Failed to toggle auto reply message");
    } finally {
      setIsLoading(false);
    }
  };

  const removeAutoReplyMessage = async (messageId, isActive) => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${baseUrl}/conv/remove-auto-reply-message`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ messageId: messageId, isActive: isActive }),
        }
      );
      const result = await response.json();
      if ("isValidToken" in result && !result?.isValidToken) {
        logOutPage();
      } else {
        if (result?.Error) {
          toast.error(result.Error);
        } else if (result?.message) {
          toast.success(result.message);
        }
      }
    } catch (err) {
      console.log("removeAutoReplyMessage err", err);
      toast.error("Failed to remove auto reply message");
    } finally {
      setIsLoading(false);
    }
  };

  const getAutoReplyMessages = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${baseUrl}/conv/get-auto-reply-message`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const autoRepliesMessages = await response.json();
      if (
        "isValidToken" in autoRepliesMessages &&
        !autoRepliesMessages?.isValidToken
      ) {
        logOutPage();
      }
      return autoRepliesMessages;
    } catch (err) {
      toast.error("Failed to get auto reply message");
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const updateLabelChat = async (objLabelChat) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${baseUrl}/conv/save-chat-label`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(objLabelChat),
      });

      const result = await response.json();
      if ("isValidToken" in result && !result?.isValidToken) {
        logOutPage();
      } else {
        if (result?.Error) {
          toast.error(result.Error);
        } else if (result?.message) {
          toast.success(result.message);
        }
      }
    } catch (err) {
      toast.error("Failed to update label on chat");
    } finally {
      setIsLoading(false);
    }
  };

  const saveMessageFlowDesign = (messageFlow) => {
    setIsLoading(true);
    fetch(`${baseUrl}/api/save-message-flow-design`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ messageFlow }),
    })
      .then((res) => res.json())
      .then((response) => {
        if ("isValidToken" in response && !response?.isValidToken) {
          logOutPage();
        } else {
          toast.success(response?.message);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        toast.error(error.Error);
        setIsLoading(false);
      });
  };

  const clearChat = async (phoneNumber) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${baseUrl}/conv/clear-chat`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ phoneNumber }),
      });

      const result = await response.json();
      if ("isValidToken" in result && !result?.isValidToken) {
        logOutPage();
      } else {
        if (response?.ok) {
          toast.success(result?.message);
        } else {
          toast.error(result?.Error || "An error occurred");
        }
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteChat = async (phoneNumber) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${baseUrl}/conv/delete-chat`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ phoneNumber }),
      });

      const result = await response.json();
      if ("isValidToken" in result && !result?.isValidToken) {
        logOutPage();
        return false;
      } else {
        if (response?.ok) {
          toast.success(result?.message);
          return true;
        } else {
          toast.error(result?.Error || "An error occurred");
          return true;
        }
      }
    } catch (error) {
      toast.error(error?.message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const saveChatbot = async (botData, chatbotId) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${baseUrl}/conv/save-chatbot`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          botData: JSON.stringify(botData, null, 2),
          chatbotId: chatbotId,
        }),
      });

      const result = await response.json();
      if ("isValidToken" in result && !result?.isValidToken) {
        logOutPage();
      } else {
        if (response?.ok) {
          toast.success(result?.message);
        } else {
          toast.error(result?.Error || "An error occurred");
        }
      }
    } catch (error) {
      toast.error(error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const getUserBots = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${baseUrl}/conv/get-chatbot`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const resp = await response.json();
      if ("isValidToken" in resp && !resp?.isValidToken) {
        logOutPage();
      }
      return resp;
    } catch (err) {
      toast.error("Failed to get user bots");
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const getUserBotsById = async (botId) => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${baseUrl}/conv/get-chatbot-byId/${botId}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const resp = await response.json();
      if ("isValidToken" in resp && !resp?.isValidToken) {
        logOutPage();
      }
      return resp;
    } catch (err) {
      toast.error("Failed to get user bots");
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const deleteChatbot = async (chatbotId) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${baseUrl}/conv/delete-chatbot`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ chatbotId }),
      });

      const result = await response.json();
      if ("isValidToken" in result && !result?.isValidToken) {
        logOutPage();
      } else {
        if (response?.ok) {
          toast.success(result?.message);
        } else {
          toast.error(result?.Error || "An error occurred");
        }
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const startContactsSync = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${baseUrl}/conv/initiate-contact-synchronization`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const result = await response.json();
      if ("isValidToken" in result && !result?.isValidToken) {
        logOutPage();
      } else {
        if (response?.ok) {
          toast.success(result?.message);
        } else {
          toast.error(result?.Error || "Caught error while syncing contacts");
        }
      }
    } catch (error) {
      toast.error(error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const startMessageHistorySync = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${baseUrl}/conv/initiate-message-history-synchronization`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const result = await response.json();
      if ("isValidToken" in result && !result?.isValidToken) {
        logOutPage();
      } else {
        if (response?.ok) {
          toast.success(result?.message);
        } else {
          toast.error(
            result?.Error || "Caught error while syncing message history"
          );
        }
      }
    } catch (error) {
      toast.error(error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const getWaFlowsSubmissions = async (botId) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${baseUrl}/conv/wa-flows-submissions`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const resp = await response.json();
      if ("isValidToken" in resp && !resp?.isValidToken) {
        logOutPage();
      }
      return resp;
    } catch (err) {
      toast.error("Failed to get WA flows data");
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ConversationContext.Provider
      value={{
        convGetChatContacts,
        convGetChatHistory,
        convGetChatMedia,
        getCampaignRepliedStatuses,
        recentContactsList,
        chatContact,
        selectedChatContactToShow,
        isShowChatHistory,
        messageNotificationCount,
        campaignRepliedStatuses,
        chatData,
        setChatData,
        chatImage,
        setChatContact,
        setSelectedChatContactToShow,
        setChatImage,
        setChatHistory,
        setMessageNotificationCount,
        quickReplyList,
        pendingApprovalQuickReply,
        notificationList,
        saveQuickReply,
        getQuickRepliesList,
        deleteQuickReply,
        approveQuickReply,
        assignChat,
        getNotifications,
        convGetAssignedChats,
        saveWelcomeMessage,
        getWelcomeMessages,
        updateMessageReadStatus,
        saveMessageFlowDesign,
        deleteWelcomeMessage,
        changeWelcomeMessagesOrder,
        createAutoReplyMessage,
        getAutoReplyMessages,
        removeAutoReplyMessage,
        updateLabelChat,
        clearChat,
        deleteChat,
        saveChatbot,
        getUserBots,
        getUserBotsById,
        deleteChatbot,
        isRecentChatLoaded,
        setRecentContactsList,
        isNewNotification,
        setIsNewNotification,
        currContact,
        setCurrContact,
        startContactsSync,
        startMessageHistorySync,
        toggleAutoReplyActive,
        getWaFlowsSubmissions,
        approvedQuickReplyList,
      }}
    >
      {children}
    </ConversationContext.Provider>
  );
};

export const useConversationContext = () => useContext(ConversationContext);
